import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { lovable } from "@/integrations/lovable";
import { toast } from "sonner";
import { Lock, Mail, UserPlus } from "lucide-react";
import medicalLogo from "@/assets/new-logo.png";
import { DesktopNav } from "@/components/DesktopNav";
import { BottomNavigation } from "@/components/BottomNavigation";

const Register = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/my-reports";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Заполните все поля");
      return;
    }
    if (password.length < 6) {
      toast.error("Пароль не короче 6 символов");
      return;
    }
    setLoading(true);
    try {
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: { emailRedirectTo: `${window.location.origin}${next.startsWith("/") ? next : "/" + next}` },
      });
      if (error) throw error;
      toast.success("Регистрация выполнена!");
      navigate(next);
    } catch (error: any) {
      toast.error(error.message || "Ошибка регистрации");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = async () => {
    setLoading(true);
    try {
      const result = await lovable.auth.signInWithOAuth("google", {
        redirect_uri: `${window.location.origin}${next.startsWith("/") ? next : "/" + next}`,
      });
      if (result.error) {
        toast.error(result.error.message || "Не удалось войти через Google");
        setLoading(false);
        return;
      }
      if (result.redirected) return;
      navigate(next);
    } catch (e: any) {
      toast.error(e.message || "Ошибка Google");
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-primary/5 to-accent/5 pt-16 lg:pt-0">
      <DesktopNav />
      <BottomNavigation />
      <div className="flex items-center justify-center py-6 px-4">
      <Card className="w-full max-w-md p-8 rounded-3xl shadow-xl border-border/50 backdrop-blur-sm bg-card/95">
        <div className="flex justify-center mb-6">
          <img src={medicalLogo} alt="Logo" className="w-16 h-16 rounded-full shadow-md object-contain" />
        </div>
        <div className="text-center mb-6">
          <h1 className="h-page text-primary">Регистрация</h1>
          <p className="text-sm text-muted-foreground">Создайте аккаунт за минуту</p>
        </div>

        <Button
          type="button"
          variant="outline"
          onClick={handleGoogle}
          disabled={loading}
          className="w-full h-12 mb-4 border-2 font-semibold gap-2"
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/>
          </svg>
          Через Google
        </Button>

        <div className="flex items-center gap-3 mb-4">
          <div className="flex-1 h-px bg-border" />
          <span className="text-xs text-muted-foreground">или email</span>
          <div className="flex-1 h-px bg-border" />
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="email" className="block text-sm font-bold mb-2">Email</label>
            <div className="relative">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="pl-12 py-6 text-base" disabled={loading}/>
            </div>
          </div>
          <div>
            <label htmlFor="password" className="block text-sm font-bold mb-2">Пароль</label>
            <div className="relative">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="не менее 6 символов" className="pl-12 py-6 text-base" disabled={loading}/>
            </div>
          </div>
          <Button type="submit" disabled={loading} className="w-full bg-gradient-to-r from-primary to-accent text-white font-bold text-base py-6 rounded-2xl shadow-lg">
            {loading ? (
              <span className="flex items-center gap-2">
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Подождите...
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <UserPlus className="w-5 h-5" />
                Зарегистрироваться
              </span>
            )}
          </Button>
          <div className="text-center text-sm">
            Уже есть аккаунт?{" "}
            <Link to={`/login?next=${encodeURIComponent(next)}`} className="text-primary font-semibold hover:underline">
              Войти
            </Link>
          </div>
          <Button type="button" variant="outline" onClick={() => navigate("/")} className="w-full">
            Вернуться на главную
          </Button>
        </form>
      </Card>
      </div>
    </div>
  );
};

export default Register;
